import axios from 'axios';
import { appConfig } from './config';

export interface AdminSession {
  token: string;
  expiresAt: string;
  user: { username: string; role: 'administrator' };
}

export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  enabled: boolean;
  updatedAt: string;
  updatedBy: string;
}

export interface DashboardData {
  periodDays: number;
  users: { total: number; newToday: number; activeToday: number };
  events: { total: number; successful: number; failed: number; successRate: number; averageDurationMs: number };
  analyses: { total: number; successful: number; failed: number };
  conversion: {
    reportsCompleted: number;
    conversionPanelsViewed: number;
    personalCtaClicks: number;
    billingViews: number;
    purchaseStarts: number;
    purchasesVerified: number;
    personalMatches: number;
    consultationClicks: number;
    consultationLeads: number;
  };
  enabledFeatureCount: number;
  totalFeatureCount: number;
  trend: Array<{ date: string; total: number; successful: number; failed: number }>;
}

export interface AnalyticsEvent {
  id: string;
  eventName: string;
  success: boolean;
  durationMs?: number;
  userId?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface AnalysisUsageDetail {
  id: string;
  stage: 'recognition' | 'report';
  provider: string;
  operation: string;
  model?: string;
  success: boolean;
  durationMs: number;
  inputTokens?: number;
  cachedInputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  estimatedCostMicros: number;
  currency: string;
  billingSource: 'provider_token_usage' | 'configured_request_rate';
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface DeepSeekBillingData {
  provider: 'DeepSeek';
  status: 'available' | 'unavailable' | 'unconfigured' | 'unsupported_base_url';
  fetchedAt: string;
  isAvailable?: boolean;
  error?: string;
  balances: Array<{
    currency: 'CNY' | 'USD';
    totalBalance: string;
    grantedBalance: string;
    toppedUpBalance: string;
  }>;
  usage: {
    periodDays: number;
    callCount: number;
    successfulCalls: number;
    analysisCount: number;
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
    totalTokens: number;
    meteredCostMicros: number;
    recognitionCostMicros: number;
    reportCostMicros: number;
    averageCostPerAnalysisMicros: number;
    currency: 'CNY';
    source: 'provider_token_usage_and_configured_prices';
  };
}

export interface AnalysisRunCost {
  id: string;
  userId: string;
  userDisplayName?: string;
  userPhoneNumber?: string;
  propertyType?: string;
  status: string;
  creditStatus: string;
  recognitionJobId?: string;
  reportJobId?: string;
  error?: string;
  createdAt: string;
  updatedAt: string;
  recognitionCompletedAt?: string;
  reportCompletedAt?: string;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  providerDurationMs: number;
  estimatedCostMicros: number;
  usageCount: number;
  usage?: AnalysisUsageDetail[];
}

export interface AdminUserRow {
  id: string;
  phoneNumber: string;
  countryCode?: string;
  displayName: string;
  registrationSource?: 'phone' | 'apple' | 'google' | 'line' | 'email';
  registrationRegion?: 'CN' | 'JP' | 'US';
  accountReference?: string;
  identityProviders: Array<'phone' | 'apple' | 'google' | 'line' | 'email'>;
  createdAt: string;
  lastLoginAt: string;
  creditsRemaining: number;
  freeCreditsGranted: number;
  subscriptionActive: boolean;
  vipActive: boolean;
  giftPlusActive?: boolean;
  giftPlusExpiresAt?: string;
  lifetimeVipActive: boolean;
  lifetimeVipProductId?: string;
  lifetimeVipPurchasedAt?: string;
  purchaseCount: number;
  consumedAnalysisCount: number;
  subscriptionProductId?: string;
  subscriptionExpiresAt?: string;
  reportCount: number;
  disabledAt?: string;
  disabledReason?: string;
  disabledBy?: string;
}

export type ConsultationStatus = 'new' | 'contacted' | 'following_up' | 'converted' | 'closed';

export interface ConsultationLead {
  id: string;
  userId: string;
  analysisRunId: string;
  name: string;
  contact: string;
  city?: string;
  topic: string;
  preferredTime?: string;
  plusPriority: boolean;
  consentedAt: string;
  status: ConsultationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface RuntimeSettingSummary {
  key: string;
  group: string;
  label: string;
  description: string;
  secret: boolean;
  multiline: boolean;
  configured: boolean;
  source: 'database' | 'environment' | 'missing';
  currentValue?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface AppleFinanceData {
  periodDays: number;
  transactions: {
    total: number;
    production: number;
    sandbox: number;
    refunded: number;
    byCurrency: Array<{
      currency: string;
      transactionCount: number;
      refundCount: number;
      grossAmount: number;
      refundedAmount: number;
    }>;
  };
  settlements: Array<{
    fiscalPeriod: string;
    currency: string;
    proceedsAmount: number;
    paidAmount: number;
    status: string;
    paidAt?: string;
    source: string;
    updatedAt: string;
  }>;
}

export interface AppleTransaction {
  transactionId: string;
  originalTransactionId: string;
  userId: string;
  productId: string;
  purchasedAt: string;
  expiresAt?: string;
  environment: string;
  priceMilliunits?: number;
  currency?: string;
  quantity: number;
  status: string;
  revokedAt?: string;
  source: 'app' | 'notification' | 'notification-history' | 'scheduled' | string;
  lastVerifiedAt?: string;
  verificationError?: string;
  userDisplayName?: string;
  userPhoneNumber?: string;
}

export interface AppleTransactionSyncResult {
  syncedAt: string;
  sourceCount: number;
  fetchedCount: number;
  insertedCount: number;
  updatedCount: number;
  failedCount: number;
  notificationFetchedCount: number;
  notificationAppliedCount: number;
  notificationFailedCount: number;
  transactions: AppleTransaction[];
}

export interface AppleReconciliationIssue {
  id: string;
  kind: 'intent' | 'notification';
  userId?: string;
  productId?: string;
  transactionId?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  error?: string;
}

export interface AppleOrderLookupTransaction {
  transactionId: string;
  originalTransactionId: string;
  productId: string;
  purchasedAt: string;
  expiresAt?: string;
  priceMilliunits?: number;
  currency?: string;
  quantity: number;
  hasAppAccountToken: boolean;
  matchedUserId?: string;
  matchedUserDisplayName?: string;
  matchedUserPhoneNumber?: string;
  alreadyRecorded: boolean;
  eligible: boolean;
  validationError?: string;
}

export interface AppleOrderLookupResult {
  orderId: string;
  environment: 'Production';
  transactions: AppleOrderLookupTransaction[];
}

export interface AppleOrderReconcileResult {
  applied: boolean;
  inserted: boolean;
  userId: string;
  transactionId: string;
  assignmentMode: 'automatic' | 'manual';
  order: AppleOrderLookupResult;
}

interface Envelope<T> { success: boolean; data: T }

const client = axios.create({ baseURL: appConfig.apiBaseUrl });
const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export async function adminLogin(username: string, password: string) {
  const response = await client.post<Envelope<AdminSession>>('/api/admin/login', { username, password });
  return response.data.data;
}

export async function fetchAdminDashboard(token: string, days = 7) {
  const response = await client.get<Envelope<DashboardData>>(`/api/admin/dashboard?days=${days}`, auth(token));
  return response.data.data;
}

export async function fetchFeatureFlags(token: string) {
  const response = await client.get<Envelope<FeatureFlag[]>>('/api/admin/features', auth(token));
  return response.data.data;
}

export async function updateFeatureFlag(token: string, key: string, enabled: boolean) {
  const response = await client.patch<Envelope<FeatureFlag>>(`/api/admin/features/${encodeURIComponent(key)}`, { enabled }, auth(token));
  return response.data.data;
}

export async function fetchAnalyticsEvents(token: string) {
  const response = await client.get<Envelope<AnalyticsEvent[]>>('/api/admin/events?limit=100', auth(token));
  return response.data.data;
}

export async function fetchAnalysisRuns(token: string) {
  const response = await client.get<Envelope<AnalysisRunCost[]>>('/api/admin/analysis-runs?limit=200', auth(token));
  return response.data.data;
}

export async function fetchAnalysisRunDetail(token: string, id: string) {
  const response = await client.get<Envelope<AnalysisRunCost>>(`/api/admin/analysis-runs/${encodeURIComponent(id)}`, auth(token));
  return response.data.data;
}

export async function fetchDeepSeekBilling(token: string, days = 30) {
  const response = await client.get<Envelope<DeepSeekBillingData>>(`/api/admin/deepseek-billing?days=${days}`, auth(token));
  return response.data.data;
}

export async function fetchAdminUsers(token: string) {
  const response = await client.get<Envelope<AdminUserRow[]>>('/api/admin/users?limit=200', auth(token));
  return response.data.data;
}

export async function fetchConsultationLeads(token: string, status?: ConsultationStatus) {
  const query = status ? `?limit=500&status=${encodeURIComponent(status)}` : '?limit=500';
  const response = await client.get<Envelope<ConsultationLead[]>>(`/api/admin/consultations${query}`, auth(token));
  return response.data.data;
}

export async function updateConsultationLeadStatus(token: string, id: string, status: ConsultationStatus) {
  const response = await client.patch<Envelope<ConsultationLead>>(
    `/api/admin/consultations/${encodeURIComponent(id)}/status`,
    { status },
    auth(token),
  );
  return response.data.data;
}

export async function setAdminUserGiftPlus(token: string, userId: string, expiresAt: string | null, reason: string) {
  const response = await client.patch<Envelope<AdminUserRow>>(
    `/api/admin/users/${encodeURIComponent(userId)}/plus-gift`, { expiresAt, reason }, auth(token),
  );
  return response.data.data;
}

export async function grantAdminUserCredits(token: string, userId: string, amount: number, reason: string, expectedBalance: number) {
  const response = await client.patch<Envelope<AdminUserRow>>(
    `/api/admin/users/${encodeURIComponent(userId)}/credits-adjustment`,
    { amount, reason, expectedBalance },
    auth(token),
  );
  return response.data.data;
}

export async function updateAdminUserStatus(
  token: string,
  userId: string,
  disabled: boolean,
  reason?: string,
) {
  const response = await client.patch<Envelope<AdminUserRow>>(
    `/api/admin/users/${encodeURIComponent(userId)}/status`,
    { disabled, reason },
    auth(token),
  );
  return response.data.data;
}

export async function fetchRuntimeSettings(token: string) {
  const response = await client.get<Envelope<RuntimeSettingSummary[]>>('/api/admin/settings', auth(token));
  return response.data.data;
}

export async function updateRuntimeSetting(token: string, key: string, value: string) {
  const response = await client.patch<Envelope<RuntimeSettingSummary>>(`/api/admin/settings/${encodeURIComponent(key)}`, { value }, auth(token));
  return response.data.data;
}

export async function restoreRuntimeSetting(token: string, key: string) {
  const response = await client.delete<Envelope<RuntimeSettingSummary>>(`/api/admin/settings/${encodeURIComponent(key)}`, auth(token));
  return response.data.data;
}

export async function fetchAppleFinance(token: string) {
  const response = await client.get<Envelope<AppleFinanceData>>('/api/admin/apple-finance?days=30', auth(token));
  return response.data.data;
}

export async function fetchAppleTransactions(token: string) {
  const response = await client.get<Envelope<AppleTransaction[]>>('/api/admin/apple-transactions?limit=500', auth(token));
  return response.data.data;
}

export async function refreshAppleTransactions(token: string) {
  const response = await client.post<Envelope<AppleTransactionSyncResult>>('/api/admin/apple-transactions/refresh', {}, auth(token));
  return response.data.data;
}

export async function fetchAppleReconciliationIssues(token: string) {
  const response = await client.get<Envelope<AppleReconciliationIssue[]>>(
    '/api/admin/apple-reconciliation-issues?limit=500',
    auth(token),
  );
  return response.data.data;
}

export async function lookupAppleOrder(token: string, orderId: string) {
  const response = await client.post<Envelope<AppleOrderLookupResult>>(
    '/api/admin/apple-orders/lookup',
    { orderId },
    auth(token),
  );
  return response.data.data;
}

export async function reconcileAppleOrder(token: string, input: {
  orderId: string;
  transactionId: string;
  userId?: string;
  confirmManualAssignment?: boolean;
}) {
  const response = await client.post<Envelope<AppleOrderReconcileResult>>(
    '/api/admin/apple-orders/reconcile',
    input,
    auth(token),
  );
  return response.data.data;
}

export async function saveAppleSettlement(token: string, input: {
  fiscalPeriod: string;
  currency: string;
  proceedsAmount: number;
  paidAmount?: number;
  status?: string;
  paidAt?: string;
}) {
  const response = await client.post<Envelope<AppleFinanceData>>('/api/admin/apple-settlements', input, auth(token));
  return response.data.data;
}
